import React, { useEffect, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const expandAnimationStyles = cssMap({
	surface: {
		display: 'grid',
		gridTemplateColumns: '0fr',
		minWidth: 0,
		opacity: 0,
		transition: 'grid-template-columns 0.2s ease-in-out, opacity 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	surfaceExpanded: {
		gridTemplateColumns: '1fr',
		opacity: 1,
	},
	clip: {
		minWidth: 0,
		overflow: 'hidden',
		// Hidden content remains mounted so quick-input drafts survive collapse, but
		// cannot receive focus from modal focus management.
		visibility: 'hidden',
		transform: 'translateX(100%)',
		willChange: 'transform',
		// Let the collapse animation finish before hiding the content. Expansion
		// overrides the delay above so the content is visible immediately.
		transition: 'transform 0.2s ease-in-out, visibility 0s linear 0.2s',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	clipExpanded: {
		visibility: 'visible',
		transform: 'translateX(0)',
		transitionDelay: '0s',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'end',
		position: 'relative',
	},
	contentRow: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'end',
		gap: token('space.0'),
		transition: 'gap 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	contentRowExpanded: {
		gap: token('space.100'),
	},
	overlay: {
		display: 'none',
		position: 'absolute',
		right: token('space.0'),
		// @ts-expect-error TS2322 cssMap does not accept percentage strings for bottom
		bottom: '100%',
		marginBottom: token('space.100'),
		zIndex: 1,
	},
	showOverlayForQuickInputFocus: {
		// @ts-expect-error TS2353 relational selectors are supported at runtime
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- keeps the overlay scoped to the quick-input interaction rather than any focus within the expander
		'&:has([data-quick-input]:focus-within, [data-quick-input-overlay]:hover, [data-quick-input-overlay]:focus-within) [data-quick-input-overlay]':
			{
				display: 'block',
			},
	},
	expandPill: {
		alignSelf: 'stretch',
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error TS2322 raw box-shadow string not in ADS shadow token union
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
	},
	expandPillClip: {
		display: 'flex',
		whiteSpace: 'nowrap',
		borderRadius: 'inherit',
	},
});

type RovoButtonExpanderProps = {
	children: React.ReactNode;
	expandedContent?: React.ReactNode;
	isExpanded?: boolean;
	isExpansionSuppressed?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	overlayContent?: React.ReactNode;
	testId?: string;
};

export function RovoButtonExpander({
	children,
	expandedContent,
	isExpanded: isExpandedProp,
	isExpansionSuppressed = false,
	onExpandedChange,
	overlayContent,
	testId,
}: RovoButtonExpanderProps): React.JSX.Element {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocusWithin, setIsFocusWithin] = useState(false);
	const isExpanded = isExpandedProp ?? (!isExpansionSuppressed && (isHovered || isFocusWithin));
	const isControlled = isExpandedProp !== undefined;

	useEffect(() => {
		onExpandedChange?.(isExpanded);
	}, [isExpanded, onExpandedChange]);

	return (
		<Box
			xcss={cx(styles.wrapper, !!overlayContent && styles.showOverlayForQuickInputFocus)}
			onMouseEnter={isControlled ? undefined : () => setIsHovered(true)}
			onMouseLeave={isControlled ? undefined : () => setIsHovered(false)}
			onFocus={isControlled ? undefined : () => setIsFocusWithin(true)}
			onBlur={(event: React.FocusEvent<HTMLElement>) => {
				if (!isControlled && !event.currentTarget.contains(event.relatedTarget as Node)) {
					setIsFocusWithin(false);
				}
			}}
		>
			<Box testId={testId} xcss={cx(styles.contentRow, isExpanded && styles.contentRowExpanded)}>
				<Box
					xcss={cx(
						expandAnimationStyles.surface,
						styles.expandPill,
						isExpanded && expandAnimationStyles.surfaceExpanded,
					)}
					data-expandable-pill
				>
					{isExpanded && overlayContent && (
						<Box xcss={styles.overlay} data-quick-input-overlay>
							{overlayContent}
						</Box>
					)}
					<Box
						xcss={cx(
							expandAnimationStyles.clip,
							styles.expandPillClip,
							isExpanded && expandAnimationStyles.clipExpanded,
						)}
						data-expandable-pill-clip
					>
						{expandedContent}
					</Box>
				</Box>
				{children}
			</Box>
		</Box>
	);
}
